import React, { useCallback, useEffect, useState } from "react";
import MedicationCreate from "../service-steps/clinical-assessment/MedicationCreate";
import DiscontinueDialog from "./DiscontinueDialog";
import { ReactSVG } from "react-svg";
import { formatDateToDD_MMM_YYYY } from "../../../../helpers/functions";
import { drugTypes, reasonOptions, therapyTypeOptions } from "../service-steps/clinical-assessment/data";
import { sourceOptions } from "../../create/options";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateMedication } from "../../../../redux/modules/clinical-services/actions";
import {
  setSelectedMedicationId,
  setSortBy,
} from "../../../../redux/modules/medications/actions";
import SubNavigation from "../common/sub-navigation";
import NewToolTip from "../../../common/New-ToolTip";
import ClinicalServiceCardToolTip from "../../../common/tooltip/clinical-service-card";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { ArrowLongUpIcon, ArrowLongDownIcon } from "@heroicons/react/24/solid";

const TREATMENT_PRESCRIPTION_NOTES_KEY = "treatmentPrescriptionNotes";
const getMedicationNotesKey = (id, medicationName) =>
  id != null && id !== ""
    ? `id:${id}`
    : `name:${String(medicationName || "").trim().toLowerCase()}`;
const readPrescriptionNotesMap = () => {
  try {
    const raw = sessionStorage.getItem(TREATMENT_PRESCRIPTION_NOTES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};
const getTherapyTypeLabel = (therapyTypeValue) => {
  if (!therapyTypeValue) return "";
  return (
    therapyTypeOptions.find((option) => option.value === therapyTypeValue)
      ?.label || therapyTypeValue
  );
};
const MedicationTable = ({
  headerName,
  records,
  showHeader = true
}) => {
  const { id: patientId } = useParams();
  const dispatch = useDispatch();
  const { selectedMedicationId } = useSelector((state) => state.medications);


  // Medication create state
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [clickedMedication, setClickedMedication] = useState(null);

  // Discontinue dialog state
  const [openDiscontinuePopup, setOpenDiscontinuePopup] = useState(false);
  const [discontinueDrug, setDiscontinueDrug] = useState(null);

  // Action menu modal state
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuPosition, setActionMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedMedicationForAction, setSelectedMedicationForAction] = useState(null);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [highlightMedicationId, setHighlightMedicationId] = useState(null);
  const [sort, setSort] = useState("drugName");
  const [order, setOrder] = useState("asc");
  const [notesOverrides, setNotesOverrides] = useState(() =>
    readPrescriptionNotesMap()
  );

  useEffect(() => {
    const syncNotesOverrides = () => {
      setNotesOverrides(readPrescriptionNotesMap());
    };
    window.addEventListener("treatmentPrescriptionNotesChanged", syncNotesOverrides);
    return () => {
      window.removeEventListener(
        "treatmentPrescriptionNotesChanged",
        syncNotesOverrides
      );
    };
  }, []);

  const resolveCommentsNotes = useCallback(
    (med) => {
      const idKey = getMedicationNotesKey(med?.id, med?.medicationName);
      const nameKey = getMedicationNotesKey(null, med?.medicationName);
      const noteOverride = notesOverrides[idKey] || notesOverrides[nameKey] || null;
      return ( med?.commentNotes || med?.commentsNotes || noteOverride?.commentNotes || "" ).toString().trim();
    },
    [notesOverrides]
  );

  // Use Redux state for highlighting
  useEffect(() => {
    setHighlightMedicationId(selectedMedicationId);
  }, [selectedMedicationId]);

  const handleActionMenuOpen = (medication, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 200; // Approximate height of the menu
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Determine if we should open above or below
    const openAbove = spaceBelow < menuHeight && spaceAbove > menuHeight;

    setActionMenuPosition({
      x: rect.right - 200, // Position menu to the left of the button
      y: openAbove ? rect.top - menuHeight - 5 : rect.bottom + 5
    });
    setSelectedMedicationForAction(medication);
    setOpenActionMenuId(medication.id);
    setShowActionMenu(true);
  };

  const handleActionMenuClose = () => {
    setShowActionMenu(false);
    setSelectedMedicationForAction(null);
    setOpenActionMenuId(null);
  };

  const updateMedicationStatus = (medication, status, discontinueData = null) => {
    const updatedMedicationObject = {
      ...medication,
      status: status,
      ...(discontinueData && {
        inactiveDate: discontinueData.inactiveDate,
        inactiveWhen: discontinueData.whenDiscontinued,
        inactiveInitiator: discontinueData.source,
        inactiveCommunicationType: discontinueData.communicationType,
        inactiveReason: discontinueData.reason,
        inactiveOtherReason: discontinueData.otherReason,
      }),
    };
    let medicationData = {
      medications: [updatedMedicationObject],
    };
    dispatch(updateMedication(patientId, medicationData));
  };

  const updateMedicationType = (medication, medicationType) => {
    const updatedMedicationObject = {
      ...medication,
      medicationType,
    };
    const medicationData = {
      medications: [updatedMedicationObject],
    };
    dispatch(updateMedication(patientId, medicationData));
  };

  // Helper function to get readable reason label from reason value
  const getReasonLabel = (reasonValue) => {
    if (!reasonValue) return null;
    const reasonOption = reasonOptions.find((reason) => reason.value === reasonValue);
    return reasonOption?.label || reasonValue;
  };

  const getSourceLabel = (sourceValue) =>
    sourceValue ? sourceOptions.find((source) => source.value === sourceValue)?.label || sourceValue : "N/A";

  // Sort medications by drug name
  const sortedRecords = [...(records || [])].sort((a, b) => {
    if (sort !== "drugName") return 0;
    const drugNameA = a?.medicationName || '';
    const drugNameB = b?.medicationName || '';
    return order === "asc" ? drugNameA.localeCompare(drugNameB) : drugNameB.localeCompare(drugNameA);
  });


  return (
    <>
      <div className="flex flex-col gap-3">
        {showHeader && (
          <div className="flex justify-between items-center pb-[10px]">
            <div className="flex gap-3 items-center">
              <h4 className="text-[22px] font-bold justify-center text-teal-300 px-[5px]">
                {headerName}
              </h4>
              <ReactSVG
                src="/icons/information-dark.svg"
                className="w-5 h-5"
                data-tooltip-id={`medication-table-info-${headerName?.toLowerCase().replace(/\s+/g, '-')}`}
              />
              <NewToolTip
                id={`medication-table-info-${headerName?.toLowerCase().replace(/\s+/g, '-')}`}
                render={() => {
                  const isPrescription = headerName?.toLowerCase().includes('prescription');
                  const isAdditional = headerName?.toLowerCase().includes('additional');

                  return (
                    <div className="bg-white p-3 flex flex-col max-w-[300px]">
                      {isPrescription ? (
                        <>
                          <p className="text-gray-800 text-sm font-semibold mb-1">Prescriptions</p>
                          <p className="text-gray-800 text-sm font-normal">Medications filled at the current pharmacy.</p>
                        </>
                      ) : isAdditional ? (
                        <>
                          <p className="text-gray-800 text-sm font-semibold mb-1">Additional Medications</p>
                          <p className="text-gray-800 text-sm font-normal">OTC, Samples, Study Drugs, Natural Products or Prescriptions filled at alternate pharmacy</p>
                        </>
                      ) : (
                        <>
                          <p className="text-gray-800 text-sm font-semibold mb-1">Prescriptions</p>
                          <p className="text-gray-800 text-sm font-normal mb-2">Medications filled at the current pharmacy.</p>
                          <p className="text-gray-800 text-sm font-semibold mb-1">Additional Medications</p>
                          <p className="text-gray-800 text-sm font-normal">OTC, Samples, Study Drugs, Natural Products or Prescriptions filled at alternate pharmacy</p>
                        </>
                      )}
                    </div>
                  );
                }}
              />
            </div>
            <SubNavigation />
          </div>
        )}
        <div className="rounded-[4px] border border-gray-100 overflow-x-auto">
          <div className="h-full overflow-y-auto">
            <table className="min-w-full ">
              <thead className="sticky top-0 z-10  bg-white">
                {/* Single Header Row */}
                <tr className="h-[52px]  bg-gray-20   font-medium text-base text-left text-gray-800 px-[10px] py-[10px]">
                  <th scope="col" className="pl-6 w-[120px]">Status</th>
                  <th scope="col" className="pl-6 w-[56px] text-center"></th>
                  <th scope="col" className="pl-2">
                    <span className="inline-flex items-center gap-3">
                      Manufacturer - Drug Name
                    </span>
                  </th>
                  <th scope="col" className="w-[33%] pl-2 border-l-[1px] border-gray-100">Prescription Details</th>
                  <th scope="col" className="w-[12%]">Therapy Type</th>
                  <th scope="col" className="w-[15%]">Comments</th>
                  <th scope="col" className="w-[56px] text-center"></th>
                </tr>
              </thead>
              <tbody className="min-w-full bg-white text-sm text-gray-800 gap-4">
                {sortedRecords?.map((med, i) => {
                  const isHighlighted = highlightMedicationId === med.id;
                  const isEvenRow = i % 2 === 0;
                  return (
                    <>
                      <tr
                        className={`min-h-[44px] ${med.status === "inactive"
                            ? "bg-red-50"
                            : isEvenRow
                              ? "bg-white"
                              : "bg-gray-10"
                          } ${isHighlighted ? "bg-blue-100" : ""} transition-colors duration-300 cursor-pointer group hover:bg-gray-50`}
                        key={med.id}
                        style={{
                          backgroundColor: isHighlighted ? '#EDF8FB' : undefined
                        }}
                        onClick={() => {
                          dispatch(setSelectedMedicationId(med.id));
                        }}
                      >
                        {/* Status */}
                        <td className="border-r-[1px] border-gray-100 py-[10px] pr-[20px] pl-[20px] gap-2 w-[120px]">
                          {/* Status Badge */}
                          <div className={`w-[47px] h-[30px] bg-transparent  rounded-y-[4px] rounded-l-[4px] p-[10px] flex items-center justify-center ${med.status === "inactive" ? "border-red-200" : "border-green-200"}`}>
                            <div className={`w-[14px] h-[14px] rounded-full ${med.status === "inactive" ? "bg-red-200" : "bg-green-200"}`}>
                            </div>
                          </div>
                        </td>

                        <td className="border-r-[1px] border-gray-100 py-[10px] pr-[20px] pl-[20px] gap-2 w-[56px] text-center">
                          {/* Action Button with Chevron */}
                          <button
                            className={`rounded-y-[4px] rounded-r-[4px] flex items-center justify-center transition-colors `}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActionMenuOpen(med, e);
                            }}
                          >
                            {openActionMenuId === med.id ? (
                              <ChevronUpIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                            ) : (
                              <ChevronDownIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                            )}
                          </button>
                        </td>

                        {/* Manufacturer - Drug Name */}
                        <td className="flex flex-col py-[10px] pr-[50px] pl-[10px] text-gray-800 text-sm font-normal align-top">
                          <span className="capitalize">
                            {med.manufacturerCode ? ` ${med.medicationName} - ${med.manufacturerCode} ` : med.medicationName}
                          </span>
                          {med.manufacturerName && (
                            <span>
                              ({med.manufacturerName})
                            </span>
                          )}
                        </td>

                        {/* Prescription Details */}
                        <td className="w-[33%] py-[10px] border-l-[1px] text-sm text-gray-800 font-normal border-gray-100 pr-[10px] pl-[10px]">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 gap-2">
                              {(() => {
                                const parts = [];
                                if (med.dosage && med.form) parts.push(`${med.dosage} ${med.form}`);
                                else if (med.form) parts.push(med.form);
                                if (med.route) parts.push(med.route);
                                if (med.frequency) parts.push(med.frequency);
                                if (med.prn) {
                                  let prnText = "PRN";
                                  if (med.prnReason) prnText += ` / ${med.prnReason}`;
                                  if (med.reason) {
                                    const reasonLabel = getReasonLabel(med.reason);
                                    prnText += ` (${reasonLabel})`;
                                  }
                                  parts.push(prnText);
                                } else if (med.reason) {
                                  // Show reason even when PRN is not true - use readable label
                                  const reasonLabel = getReasonLabel(med.reason);
                                  parts.push(reasonLabel);
                                }

                                const details = parts.length > 0 ? parts.join(", ") : "N/A";
                                const duration = med.duration ? med.unit ? `${med.duration} ${med.unit}` : `${med.duration}` : "N/A";
                                const startDate = med.duration && med.startDate ? formatDateToDD_MMM_YYYY(new Date(med.startDate)) : "N/A";
                                const endDate = med.duration && med.endDate ? formatDateToDD_MMM_YYYY(new Date(med.endDate)) : "N/A";
                                const specialInstructions = (med.comments || "").toString().trim();
                                const commentsNotes = resolveCommentsNotes(med);
                                // Prefer `dispenseQuantity` (new payload) but fall back to legacy `quantity`
                                const rawQuantity = med.dispenseQuantity || med.quantity || null;
                                const quantity = rawQuantity ? (typeof rawQuantity === 'object' ? (rawQuantity.value || rawQuantity.label) : `${rawQuantity}`) : "N/A";
                                const refills = med.refills ? `${med.refills}` : "N/A";
                                const extraMedicationDetails = [];
                                if (specialInstructions) {
                                  extraMedicationDetails.push(`Special Instructions: ${specialInstructions}`);
                                }
                                return (
                                  <span>
                                    {`${details}`}
                                    <br />
                                    {`Duration: ${duration}, Start Date: ${startDate}, End Date: ${endDate}`}
                                    <br />
                                    {`  QTY ${quantity}, Refills ${refills}${extraMedicationDetails.length > 0 ? `, ${extraMedicationDetails.join(", ")}` : ""}`}
                                  </span>
                                );
                              })()}

                              {openActionMenuId === `details-${med.id}` && (
                                <div className="flex text-sm text-gray-800 mt-[8px]">
                                  {/* <span>
                                  Rx Number, DIN # : {med.medicationGenCode}, First Fill Date : 5-Apr-2000, Last Filled Date : Nov-2020 , Prescriber Name : {med.prescriberName}
                                </span> */}
                                  <span>
                                    Prescriber Name : {med.prescriberName}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Full-width info bar under the cell (still within Prescription Details) */}
                          {openActionMenuId === `details-${med.id}` && (
                            <div className=" -ml-[12px] -mr-[51px] gap-[10px]">
                              <div className="flex w-full py-[5px] justify-start gap-4 bg-yellow-60 border-b border-t border-b-yellow-75 border-t-yellow-75 px-[12px]">
                                <div className="whitespace-nowrap">
                                  <span>Adherence Status: Coming Soon</span>
                                </div>
                                <div className="flex items-center gap-2 whitespace-nowrap">
                                  <span className="pl-36">Source: {getSourceLabel(med.creator || med.source)}</span>
                                  <ReactSVG 
                                    src="/icons/information-dark.svg" 
                                    className="w-4 h-4" 
                                    data-tooltip-id="information-source-tooltip"
                                  />
                                  <NewToolTip
                                    id="information-source-tooltip"
                                    render={() => (
                                      <ClinicalServiceCardToolTip
                                        noteTitle="Prescription Details"
                                        noteDescription={
                                          <>
                                            The Adherence Status will auto populate after a Medication Review has been completed.
                                            <br /> <br />
                                            Rx Number, DIN #, First Filled Date, Last Filled Date will auto populate once there is integration with the Pharmacy Management System.
                                          </>
                                        }
                                      />
                                    )}
                                    clickable={true}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                        {/* Therapy Type */}
                        <td className="w-[12%] py-[10px] text-sm text-gray-800 font-normal pr-[6px] pl-[8px] align-top">
                          <span>{getTherapyTypeLabel(med.therapyType)}</span>
                        </td>
                        {/* Comments / Notes */}
                        <td className="w-[15%] py-[10px] text-sm text-gray-800 font-normal pr-[6px] pl-[8px] align-top">
                          {(() => {
                            const commentsNotes = resolveCommentsNotes(med);
                            return commentsNotes ? (
                              <span>{`${commentsNotes}`}</span>
                            ) : (
                              <span></span>
                            );
                          })()}
                        </td>
                        {/* Chevron (last column) */}
                        <td className="w-[56px] py-[10px] pl-[2px] text-left align-top">
                          <button
                            className="px-[2px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenuId((prev) => (prev === `details-${med.id}` ? null : `details-${med.id}`));
                            }}
                          >
                            {openActionMenuId === `details-${med.id}` ? (
                              <ChevronUpIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                            ) : (
                              <ChevronDownIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                            )}
                          </button>
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(!records || records.length === 0) && (
            <div className="p-8 text-center text-gray-500">
              <p>No medications found</p>
            </div>
          )}
        </div>

        {/* Custom Action Menu Modal */}
        {showActionMenu && selectedMedicationForAction && (
          <div
            className="fixed inset-0 z-50 ml-48"
            onClick={handleActionMenuClose}
          >
            <div
              className="flex flex-col absolute bg-white shadow-lg border border-gray-200 rounded-[4px] w-50"
              style={{
                left: `${actionMenuPosition.x}px`,
                top: `${actionMenuPosition.y}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={`${selectedMedicationForAction?.status === "inactive" &&
                  "opacity-40 pointer-events-none text-gray-200"
                  } flex w-full text-left text-sm font-normal text-gray-800 pl-[14px] pr-7 py-[5px]  border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  setOpenDiscontinuePopup(true);
                  setDiscontinueDrug(selectedMedicationForAction);
                  handleActionMenuClose();
                }}
              >
                Discontinue
              </button>
              <button
                disabled={true}
                className="opacity-40 pointer-events-none flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px]  border-b-gray-50 hover:bg-gray-50"
              >
                History
              </button>
              <button
                className="opacity-40 flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px]  border-b-gray-50 hover:bg-gray-50"
                disabled={true}
              >
                Hold
              </button>
              <button
                className="flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px]  border-b-gray-50 hover:bg-gray-50"
                onClick={() => {
                  setClickedMedication(selectedMedicationForAction);
                  setShowMedicationSlider(true);
                  handleActionMenuClose();
                }}
              >
                Modify
              </button>
              <button
                className={`${
                  selectedMedicationForAction?.medicationType ===
                  drugTypes.Prescription
                    ? "opacity-40 pointer-events-none"
                    : ""
                } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px] border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  updateMedicationType(
                    selectedMedicationForAction,
                    drugTypes.Prescription
                  );
                  handleActionMenuClose();
                }}
              >
                Move to Prescriptions
              </button>
              <button
                className={`${
                  selectedMedicationForAction?.medicationType ===
                  drugTypes.Additional
                    ? "opacity-40 pointer-events-none"
                    : ""
                } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px] border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  updateMedicationType(
                    selectedMedicationForAction,
                    drugTypes.Additional
                  );
                  handleActionMenuClose();
                }}
              >
                Move to Additional Medications
              </button>
              <button
                className={`${
                  selectedMedicationForAction?.medicationType === drugTypes.Vaccines
                    ? "opacity-40 pointer-events-none"
                    : ""
                } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px] border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  updateMedicationType(
                    selectedMedicationForAction,
                    drugTypes.Vaccines
                  );
                  handleActionMenuClose();
                }}
              >
                Move to Vaccines
              </button>
              <button
                disabled={selectedMedicationForAction?.status === "active"}
                className={` ${selectedMedicationForAction?.status === "active" &&
                  "opacity-40 pointer-events-none"
                  } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px]  border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  // Void functionality
                  handleActionMenuClose();
                }}
              >
                Void
              </button>
              <button
                disabled={selectedMedicationForAction?.status === "active"}
                className={` ${selectedMedicationForAction?.status === "active" &&
                  "opacity-40 pointer-events-none"
                  } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[5px]  border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  updateMedicationStatus(selectedMedicationForAction, "active");
                  handleActionMenuClose();
                }}
              >
                Resume
              </button>
            </div>
          </div>
        )}

        {/* Medication Create Modal */}
        <MedicationCreate
          create={clickedMedication?.id ? false : true}
          patientId={patientId}
          type="Medication"
          open={showMedicationSlider}
          fullMedication={clickedMedication}
          step={1}
          maxSteps={1}
          onClose={(value) => {
            setClickedMedication(null);
            setShowMedicationSlider(value);
          }}
          onSave={() => {
            setShowMedicationSlider(false);
          }}
        />

        {/* Discontinue Dialog */}
        <DiscontinueDialog
          open={openDiscontinuePopup}
          drug={discontinueDrug}
          onCross={() => {
            setOpenDiscontinuePopup(false);
            setDiscontinueDrug(null);
          }}
          handleClose={() => {
            setMenuMedication(null);
            setOpenDiscontinuePopup(false);
            setDiscontinueDrug(null);
          }}
          confirmAction={(discontinueData) => {
            updateMedicationStatus(discontinueDrug, "inactive", discontinueData);
            setOpenDiscontinuePopup(false);
            setDiscontinueDrug(null);
          }}
        />
      </div>
    </>
  );
};

export default MedicationTable;
